import { useInView } from 'motion/react'
import { useRef } from 'react'
import { cn } from '#app/utils/misc'
import type { FeatureCopy, TileColour } from '#app/utils/site/content'
import { BigHeading, Reveal } from '../primitives'
import { SCENES } from './scenes'

const TILE_BG: Record<TileColour, string> = {
	pink: 'bg-tt-tile-pink',
	lav: 'bg-tt-tile-lav',
	green: 'bg-tt-tile-green',
}

/**
 * One feature row: headline and paragraph beside an animated product tile.
 * Rows alternate sides on desktop; the tile owns the in-view observer and
 * hands `active` to its scene so animation only runs while it is visible.
 */
export function FeatureRow({
	feature,
	index,
}: {
	feature: FeatureCopy
	index: number
}) {
	const tileRef = useRef<HTMLElement>(null)
	const inView = useInView(tileRef, { amount: 0.35 })
	const Scene = SCENES[feature.id]
	const flip = index % 2 === 1

	return (
		<Reveal
			className={cn(
				'grid items-center gap-[30px] py-9 min-[900px]:grid-cols-2 min-[900px]:gap-[72px] min-[900px]:py-14',
				feature.wide &&
					'min-[900px]:grid-cols-[1.25fr_.75fr] min-[900px]:gap-14',
				feature.visualWide &&
					'min-[900px]:grid-cols-[.9fr_1.1fr] min-[900px]:gap-14',
				feature.compactOnPhone &&
					'max-[520px]:gap-4 max-[520px]:py-6 max-[520px]:[&_h2]:text-[36px] max-[520px]:[&_p]:mt-3 max-[520px]:[&_p]:text-[16px] max-[520px]:[&_p]:leading-[1.45]',
			)}
		>
			<div className={cn('min-w-0', flip && 'min-[900px]:order-2')}>
				<BigHeading as="h2" className="text-[clamp(46px,5.6vw,84px)]">
					{feature.heading}
				</BigHeading>
				<p className="mt-[22px] max-w-[32ch] text-[19px] leading-[1.55] text-tt-body">
					{feature.body}
				</p>
			</div>
			<section
				ref={tileRef}
				aria-label={feature.ariaLabel}
				className={cn(
					'relative flex min-w-0 items-center justify-center overflow-hidden rounded-[26px] px-5 py-[30px] min-[900px]:min-h-[440px] min-[900px]:rounded-[36px] min-[900px]:p-11',
					TILE_BG[feature.tile],
					flip && 'min-[900px]:order-1',
				)}
			>
				<Scene active={inView} />
			</section>
		</Reveal>
	)
}
